import React, { createContext, useContext, useState, useEffect } from 'react';
import { playThemeSwitch } from '../utils/SoundManager';

const ThemeContext = createContext();

// الصفحات التي لا نريد فيها الوضع المظلم
const PUBLIC_PAGES = ['/', '/login', '/oath', '/forgot-password', '/reset-password', '/terms', '/privacy'];

export function ThemeProvider({ children }) {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
    
    // التحقق من المسار الحالي
    const currentPath = window.location.pathname;
    const isPublicPage = PUBLIC_PAGES.includes(currentPath);
    
    // تطبيق الألوان على الجذر - فقط إذا لم نكن في صفحة عامة
    if (isDarkMode && !isPublicPage) {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
  }, [isDarkMode]);
  
  // مراقبة تغيير المسار
  useEffect(() => {
    const handleLocationChange = () => {
      const currentPath = window.location.pathname;
      const isPublicPage = PUBLIC_PAGES.includes(currentPath);
      
      if (isPublicPage) {
        document.documentElement.removeAttribute('data-theme');
      } else if (isDarkMode) {
        document.documentElement.setAttribute('data-theme', 'dark');
      }
    };
    
    // مراقبة تغييرات المسار
    window.addEventListener('popstate', handleLocationChange);
    
    // تنفيذ عند التحميل الأول
    handleLocationChange();
    
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, [isDarkMode]);

  const toggleTheme = () => {
    // التحقق أننا لسنا في صفحة عامة
    const currentPath = window.location.pathname;
    const isPublicPage = PUBLIC_PAGES.includes(currentPath);
    
    if (isPublicPage) {
      return; // لا نسمح بتغيير الوضع في الصفحات العامة
    }
    
    // تشغيل صوت التبديل
    playThemeSwitch();
    setIsDarkMode(prev => !prev);
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

// صوت تبديل الوضع
function playToggleSound(isDark) {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    if (isDark) {
      // صوت الصباح (مرتفع ومبهج)
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(1200, audioContext.currentTime + 0.1);
      oscillator.frequency.exponentialRampToValueAtTime(1000, audioContext.currentTime + 0.2);
    } else {
      // صوت الليل (منخفض وهادئ)
      oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(200, audioContext.currentTime + 0.15);
      oscillator.frequency.exponentialRampToValueAtTime(150, audioContext.currentTime + 0.3);
    }
    
    oscillator.type = 'sine';
    gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (e) {
    console.log('Audio not supported');
  }
}

// زر تبديل الوضع - مصباح
export function ThemeToggle() {
  const { isDarkMode, toggleTheme } = useTheme();
  
  return (
    <button
      onClick={toggleTheme}
      data-testid="theme-toggle-btn"
      style={{
        background: isDarkMode 
          ? 'linear-gradient(135deg, #2d2d44 0%, #1a1a2e 100%)' 
          : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        border: isDarkMode ? '2px solid #444' : '2px solid #ddd',
        borderRadius: '10px',
        width: '40px',
        height: '40px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s ease',
        position: 'relative',
        boxShadow: isDarkMode 
          ? 'inset 0 2px 4px rgba(0,0,0,0.3)' 
          : '0 2px 8px rgba(0,0,0,0.1)',
      }}
      title={isDarkMode ? 'تفعيل الوضع النهاري' : 'تفعيل الوضع الليلي'}
    >
      {/* أيقونة المصباح */}
      <svg 
        width="22" 
        height="22" 
        viewBox="0 0 24 24" 
        fill="none"
        style={{
          transition: 'all 0.3s ease',
        }}
      >
        {/* قاعدة المصباح */}
        <rect x="9" y="18" width="6" height="2" rx="1" fill={isDarkMode ? '#666' : '#888'} />
        <rect x="9" y="20" width="6" height="1" rx="0.5" fill={isDarkMode ? '#555' : '#777'} />
        
        {/* جسم المصباح */}
        <path 
          d="M9 18V15C6.5 13.5 5 11 5 8C5 4.13 8.13 1 12 1C15.87 1 19 4.13 19 8C19 11 17.5 13.5 15 15V18H9Z" 
          fill={isDarkMode ? '#FFD700' : '#E8E8E8'}
          stroke={isDarkMode ? '#FFA500' : '#CCC'}
          strokeWidth="1"
        />
        
        {/* الضوء المنبعث (فقط عند الإضاءة) */}
        {isDarkMode && (
          <>
            <circle cx="12" cy="8" r="3" fill="#FFF5CC" opacity="0.8" />
            <circle cx="12" cy="8" r="5" fill="#FFE066" opacity="0.3" />
          </>
        )}
        
        {/* خيوط الضوء */}
        {isDarkMode && (
          <g stroke="#FFD700" strokeWidth="1.5" strokeLinecap="round" opacity="0.6">
            <line x1="12" y1="-2" x2="12" y2="0" />
            <line x1="4" y1="8" x2="2" y2="8" />
            <line x1="20" y1="8" x2="22" y2="8" />
            <line x1="5.5" y1="2.5" x2="4" y2="1" />
            <line x1="18.5" y1="2.5" x2="20" y2="1" />
          </g>
        )}
      </svg>
    </button>
  );
}

export default ThemeContext;
